import { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { fetchUserProfile, fetchUserProfiles } from './userProfiles'

// 🔒 caches
const usersCache = {}
const channelMembersCache = {}

// A chat's members are the case the rules cannot cover: accepting an incident
// puts you on its chat, and the other crew may be in an organisation we hold no
// dispatch binding to. The batched `documentId() in` read this used to do was
// refused OUTRIGHT for the whole group when one member was unreadable, so a
// single cross-organisation responder turned every name in the chat into
// "Unknown". fetchUserProfiles falls back to getUserProfiles per group.
const fetchUsersByIds = async (ids) => {
  const uncached = ids.filter((id) => !usersCache[id])
  if (uncached.length === 0) return

  const byId = await fetchUserProfiles(uncached)
  Object.entries(byId).forEach(([id, user]) => {
    usersCache[id] = user
  })
}

export const useUserDisplayName = (userId, showLastName, channelId) => {
  const [name, setName] = useState('')

  useEffect(() => {
    if (!userId || !channelId) return

    let mounted = true

    const load = async () => {
      if (!channelMembersCache[channelId]) {
        const channelSnap = await getDoc(doc(db, 'chats', channelId))

        if (!channelSnap.exists()) return

        channelMembersCache[channelId] = channelSnap.data().memberIds || []
      }

      const memberIds = channelMembersCache[channelId]
      if (memberIds.length === 0) return

      await fetchUsersByIds(memberIds)

      if (!mounted) return

      const user = usersCache[userId]
      if (!user) {
        setName('Unknown')
        return
      }

      const first = user.firstName || ''
      const last = user.lastName || ''
      const display = user.displayName || first

      // A user resolved through getUserProfiles carries displayName and no
      // firstName or lastName — the projection deliberately withholds them. Fall
      // back to the display name rather than rendering an empty surname.
      setName(showLastName && last ? `${last}` : display || 'Unknown')
    }

    load()

    return () => {
      mounted = false
    }
  }, [userId, showLastName, channelId])

  return name
}

export const useTargetUserName = (targetUserId, showLastName) => {
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!targetUserId || showLastName === undefined) return;

    const fetchUser = async () => {
      try {
        setLoading(true);

        const userData = await fetchUserProfile(targetUserId);

        if (userData) {
          // lastName is absent from the getUserProfiles projection, so
          // displayName carries the fallback.
          setUserName(
            (showLastName ? userData.lastName : userData.displayName) ||
              userData.displayName ||
              "Unknown User"
          );
        } else {
          setUserName("Unknown User");
        }
      } catch (error) {
        console.error("Error fetching target user:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [targetUserId, showLastName]);

  return { userName, loading };
};
